import type { CSSProperties } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { parseShareToken, type SharedBendSetup } from '../lib/share.js';
import { CALCULATORS } from './calculator-configs.js';
import { formatInches } from '../lib/format.js';
import { setStoredCalculatorValues } from '../lib/calculator-values.js';
import { LAST_BENDER_STORAGE_KEY } from '../lib/app-settings.js';
import { normalizeBenderSelection } from '../lib/bender-selection.js';
import * as storage from '../lib/storage.js';

const FIELD_LABELS: Record<string, string> = {
  distanceBetweenMarks: 'Between bends',
  secondMark: 'Mark 2',
  firstMark: '1st mark',
  thirdMark: '3rd mark',
  firstSideMark: '1st mark',
  secondSideMark: '3rd mark',
  centerMark: 'Center mark',
  centerBend: 'Center bend',
  centerAngle: 'Center bend',
  sideAngle: 'Side bends',
  sideBendAngle: 'Side bends',
  angle: 'Bend',
  bendAngle: 'Bend',
  shrink: 'Shrink',
  takeUp: 'Take-up',
  deduct: 'Deduct',
  gain: 'Gain',
  progression: 'Each add',
  totalLength: 'Total length',
};

const DEGREE_FIELDS = new Set([
  'angle',
  'bendAngle',
  'centerAngle',
  'centerBend',
  'sideAngle',
  'sideBendAngle',
  'kickAngle',
  'totalAngle',
  'segmentAngle',
]);

const HIDDEN_FIELDS = new Set(['type']);
const SADDLE4_MARK_LABELS = ['First mark', 'Second mark', 'Third mark', 'Fourth mark'] as const;

export default function ShareReceiverScreen() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const payload = token ? parseShareToken(token) : null;

  if (!payload) {
    return (
      <div style={shareShellStyle}>
        <section aria-label="Invalid shared bend" style={invalidCardStyle}>
          <div style={eyebrowStyle}>Shared bend</div>
          <h1 style={titleStyle}>Share link invalid</h1>
          <p style={invalidCopyStyle}>
            This shared bend could not be opened. The link may be incomplete or expired.
          </p>
          <Link to="/" style={openCalculatorLinkStyle}>
            Back to bends
          </Link>
        </section>
      </div>
    );
  }

  const bend = payload.bend;
  const cfg = CALCULATORS.find((c) => c.id === bend.calculatorId);
  const fields = sharedResultFields(bend.result);
  const setupLabel = sharedSetupLabel(bend.setup);
  return (
    <div style={shareShellStyle}>
      <div style={eyebrowStyle}>Shared bend</div>
      <h1 style={titleStyle}>{cfg?.displayName ?? bend.calculatorId}</h1>
      {setupLabel && <p style={setupLabelStyle}>{setupLabel}</p>}
      <div style={resultGridStyle}>
        {fields.map((field) => (
          <div key={field.key} style={resultCardStyle}>
            <span style={resultLabelStyle}>{field.label}</span>
            <span className="mono" style={resultValueStyle}>
              {field.value}
            </span>
          </div>
        ))}
      </div>
      {cfg &&
        (bend.values || bend.bender ? (
          <button
            type="button"
            aria-label={`Open ${cfg.displayName} with these values`}
            style={openCalculatorButtonStyle}
            onClick={() => {
              if (bend.values) setStoredCalculatorValues(cfg.id, bend.values);
              if (bend.bender) {
                storage.set(LAST_BENDER_STORAGE_KEY, normalizeBenderSelection(bend.bender));
              }
              navigate(`/${cfg.id}`);
            }}
          >
            {bend.values ? 'Open with these values' : 'Open with this setup'}
          </button>
        ) : (
          <Link to={`/${cfg.id}`} style={openCalculatorLinkStyle}>
            Open {cfg.displayName}
          </Link>
        ))}
    </div>
  );
}

function sharedSetupLabel(setup: SharedBendSetup | undefined): string | null {
  const parts = [setup?.conduitLabel, setup?.benderLabel].filter((part): part is string =>
    Boolean(part),
  );
  return parts.length > 0 ? parts.join(' / ') : null;
}

interface SharedResultField {
  key: string;
  label: string;
  value: string;
}

function sharedResultFields(result: Record<string, unknown>): SharedResultField[] {
  const type = typeof result.type === 'string' ? result.type : '';
  if (type === 'saddle4') return sharedSaddle4Fields(result);
  if (type === 'parallel-offset') return sharedParallelOffsetFields(result);
  if (type === 'concentric') return sharedConcentricFields(result);

  return Object.entries(result)
    .filter(([key, value]) => !HIDDEN_FIELDS.has(key) && typeof value === 'number')
    .map(([key, value]) => ({
      key,
      label: FIELD_LABELS[key] ?? humanizeFieldName(key),
      value: formatSharedValue(key, Number(value)),
    }));
}

function sharedSaddle4Fields(result: Record<string, unknown>): SharedResultField[] {
  const marks = numberArray(result.marks);
  return [
    ...marks.slice(0, 4).map((mark, index) => ({
      key: `mark-${index + 1}`,
      label: SADDLE4_MARK_LABELS[index] ?? `Mark ${index + 1}`,
      value: formatInches(mark),
    })),
    ...flatNumberFields(result, ['marks']),
  ];
}

function sharedParallelOffsetFields(result: Record<string, unknown>): SharedResultField[] {
  const runs = Array.isArray(result.runs) ? result.runs : [];
  return [
    ...flatNumberFields(result, ['runs', 'totalLength']),
    ...runs.flatMap((run, index) => {
      if (!isRecord(run)) return [];
      const runNumber = numberValue(run.run) ?? index + 1;
      const firstMark = numberValue(run.firstMark);
      const secondMark = numberValue(run.secondMark);
      const fields: SharedResultField[] = [];
      if (firstMark !== undefined) {
        fields.push({
          key: `run-${runNumber}-mark-1`,
          label: `Run ${runNumber} mark 1`,
          value: formatInches(firstMark),
        });
      }
      if (secondMark !== undefined) {
        fields.push({
          key: `run-${runNumber}-mark-2`,
          label: `Run ${runNumber} mark 2`,
          value: formatInches(secondMark),
        });
      }
      return fields;
    }),
  ];
}

function sharedConcentricFields(result: Record<string, unknown>): SharedResultField[] {
  const parallels = Array.isArray(result.parallels) ? result.parallels.filter(isRecord) : [];
  if (parallels.length === 0) return [];

  const last = parallels[parallels.length - 1]!;
  const fields: SharedResultField[] = [];
  const outermostAdd = numberValue(last.additionalLength);
  if (outermostAdd !== undefined) {
    fields.push({
      key: 'outermost-add',
      label: 'Outermost add',
      value: formatInches(outermostAdd),
    });
  }

  parallels.slice(0, -1).forEach((parallel, index) => {
    const additionalLength = numberValue(parallel.additionalLength);
    if (additionalLength === undefined) return;
    fields.push({
      key: `run-${index + 1}-add`,
      label: index === 0 ? 'Inner run add' : `Run ${index + 1} add`,
      value: formatInches(additionalLength),
    });
  });

  return fields;
}

function flatNumberFields(
  result: Record<string, unknown>,
  extraHiddenFields: readonly string[] = [],
): SharedResultField[] {
  const hidden = new Set([...HIDDEN_FIELDS, ...extraHiddenFields]);
  return Object.entries(result)
    .filter(([key, value]) => !hidden.has(key) && typeof value === 'number')
    .map(([key, value]) => ({
      key,
      label: FIELD_LABELS[key] ?? humanizeFieldName(key),
      value: formatSharedValue(key, Number(value)),
    }));
}

function numberArray(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is number => typeof item === 'number' && Number.isFinite(item));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function numberValue(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function formatSharedValue(key: string, value: number): string {
  if (DEGREE_FIELDS.has(key)) return formatDegrees(value);
  return formatInches(value);
}

function formatDegrees(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}\u00b0`;
}

function humanizeFieldName(key: string): string {
  return key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, (first) => first.toUpperCase());
}

const shareShellStyle: CSSProperties = {
  display: 'grid',
  gap: 'var(--space-3)',
};

const eyebrowStyle: CSSProperties = {
  fontSize: 'var(--text-xs)',
  color: 'var(--color-accent)',
  fontWeight: 900,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
};

const titleStyle: CSSProperties = {
  fontSize: 'var(--text-2xl)',
  lineHeight: 1.05,
  margin: 0,
};

const setupLabelStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--text-sm)',
  fontWeight: 800,
  lineHeight: 1.25,
  margin: 0,
};

const resultGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 'var(--space-2)',
};

const resultCardStyle: CSSProperties = {
  minHeight: 78,
  display: 'grid',
  alignContent: 'center',
  gap: 'var(--space-1)',
  padding: 'var(--space-3)',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
};

const resultLabelStyle: CSSProperties = {
  color: 'var(--color-text-muted)',
  fontSize: 'var(--text-xs)',
  fontWeight: 900,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
};

const resultValueStyle: CSSProperties = {
  color: 'var(--color-text-primary)',
  fontSize: 'var(--text-2xl)',
  fontWeight: 850,
  lineHeight: 1,
};

const openCalculatorLinkStyle: CSSProperties = {
  minHeight: 48,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--color-text-on-orange)',
  background: 'var(--color-accent)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-3) var(--space-5)',
  fontWeight: 900,
  textDecoration: 'none',
  boxShadow: '0 14px 28px rgba(249, 115, 22, 0.24)',
};

const openCalculatorButtonStyle: CSSProperties = {
  ...openCalculatorLinkStyle,
  border: 'none',
  cursor: 'pointer',
};

const invalidCardStyle: CSSProperties = {
  display: 'grid',
  gap: 'var(--space-3)',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-5)',
  boxShadow: 'var(--shadow-card)',
};

const invalidCopyStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--text-base)',
  lineHeight: 1.45,
  margin: 0,
};
